import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, EneOutageProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { eneGlyph } from './markerGlyphs.js';
import type { IconDef } from './markerGlyphs.js';

const eneProps = (f: LayerFeature): EneOutageProps => f.props as unknown as EneOutageProps;

// Icon sizes per ADA status (logical pixels). ADA-path outages render slightly larger.
const SIZE_ADA = 26;
const SIZE_ROUTINE = 22;

/** Style an outage icon by ADA impact: accessible-path outages pop red and larger;
 * routine (non-ADA) outages are dim amber and smaller. */
export function eneStyle(ada: boolean): { color: [number, number, number]; radius: number } {
  return ada ? { color: [239, 68, 68], radius: 8 } : { color: [180, 150, 90], radius: 6 };
}

interface EneDatum {
  position: [number, number];
  icon: IconDef;
  size: number;
  props: EneOutageProps;
}

/**
 * Build the elevator/escalator outage layer: an IconLayer of currently-out
 * equipment, using a rounded sign badge glyph with elevator (↑↓ arrows) or
 * escalator (diagonal steps) pictogram, colored by ADA impact. Center-anchored
 * so the badge sits over the equipment location. Static markers (data rebuilt
 * each frame, so no updateTriggers). Returns null when the feed is down/empty.
 */
export function buildEneLayer(state: LayerState, _nowSec: number): IconLayer<EneDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const data: EneDatum[] = state.features.map((f) => {
    const p = eneProps(f);
    const { color } = eneStyle(p.ada);
    const size = p.ada ? SIZE_ADA : SIZE_ROUTINE;
    return {
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      icon: eneGlyph(p.kind, p.ada, color, size),
      size,
      props: p,
    };
  });

  return new IconLayer<EneDatum>({
    id: 'ene',
    data,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: (d) => d.size,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const eneRenderer: RendererDef = {
  layerId: 'ene',
  build: (state, nowSec) => buildEneLayer(state, nowSec),
};
